import { useState, useRef, useEffect } from 'react'
import {
  useCreateExternalAgentMutation,
  useFinalizeAgentMutation,
  useDiscardDraftAgentMutation,
  useGetKnowledgeFilesQuery,
  useDeleteKnowledgeFileMutation,
  useLazyCheckAgentNameQuery,
  useGetHostedProvidersQuery,
} from '../../store/api/developerApi'
import { MultiSelectDropdown } from '../../components/ui/MultiSelectDropdown'
import type { Category } from '../../store/api/categoriesApi'
import { api, ApiError } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { AGENT_NAME_PATTERN, AGENT_NAME_RULE } from '@ekkam/shared'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { AgentSubscriptionModal } from '../admin/AgentSubscriptionModal'
import {
  AGENT_DOC_MAX_FILES,
  AGENT_DOC_MAX_FILE_SIZE_BYTES,
  FILE_PROCESSING_POLL_MS,
} from '../../lib/config'
import s from '../../styles/pages/DeveloperPage.module.css'

const EMOJI_ICON_MAX_LEN = 2

interface Props {
  categories: Category[]
  onAgentCreated: () => void
}

export function CreatorMethod1Tab({ categories, onAgentCreated }: Props) {
  const [name, setName]               = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon]               = useState('')
  const [color, setColor]             = useState('#7c3aed')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [catIds, setCatIds]           = useState<string[]>([])
  const [suggestedCatName, setSuggestedCatName] = useState('')
  const [nameError, setNameError]     = useState<string | null>(null)
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null)
  const [checkingName, setCheckingName]   = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [agentId, setAgentId]         = useState<string | null>(null)
  const [agentStatus, setAgentStatus] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [uploadNotice, setUploadNotice] = useState('')
  const [uploading, setUploading]     = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  // File a 402 (subscription required) interrupted — replayed automatically
  // once payment goes through.
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { accessToken } = useAppSelector(st => st.auth)

  const { data: providersData } = useGetHostedProvidersQuery()
  const providers = providersData?.providers ?? {}
  const [hostedProvider, setHostedProvider] = useState('groq')
  const [hostedModel, setHostedModel]       = useState('')

  useEffect(() => {
    if (!hostedModel && hostedProvider && providers[hostedProvider]?.models?.length) {
      setHostedModel(providers[hostedProvider].models[0])
    }
  }, [providers, hostedProvider])

  const [createAgent, { isLoading: creating }]   = useCreateExternalAgentMutation()
  const [finalizeAgent, { isLoading: finalizing }] = useFinalizeAgentMutation()
  const [discardDraft]  = useDiscardDraftAgentMutation()
  const [checkName]     = useLazyCheckAgentNameQuery()
  const [deleteFile]    = useDeleteKnowledgeFileMutation()

  const { data: kbData, refetch: refetchKb } = useGetKnowledgeFilesQuery(agentId ?? '', {
    skip: !agentId,
    pollingInterval: FILE_PROCESSING_POLL_MS,
  })
  const knowledgeFiles = agentId ? (kbData?.files ?? []) : []

  const isDraft    = agentStatus === 'draft'
  const isPending  = agentStatus === 'pending_review'
  const isActive   = agentStatus === 'active'
  const isSubmitted = isPending || isActive
  const hasReadyFile = knowledgeFiles.some(f => f.status === 'ready')

  const nameFormatError = name.trim() && !AGENT_NAME_PATTERN.test(name.trim())
    ? AGENT_NAME_RULE
    : null

  const handleNameBlur = async () => {
    const trimmed = name.trim()
    if (!trimmed || nameFormatError) return
    setCheckingName(true); setNameAvailable(null); setNameError(null)
    try {
      const res = await checkName(trimmed).unwrap()
      setNameAvailable(res.available)
      if (!res.available) setNameError(`"${trimmed}" is already taken. Please choose a different name.`)
    } catch { setNameAvailable(null) }
    finally { setCheckingName(false) }
  }

  const handleSaveDraft = async () => {
    setSubmitError('')
    if (!name.trim() || nameFormatError || nameAvailable === false) return
    try {
      if (!agentId) {
        const res = await createAgent({
          name: name.trim(), description: description.trim(),
          icon: icon.trim() || undefined, color: color.trim() || undefined,
          systemPrompt: systemPrompt.trim(), categoryIds: catIds,
          suggestedCategoryName: suggestedCatName.trim() || null,
          agentType: 'hosted', hostedProvider, hostedModel,
        }).unwrap()
        setAgentId(res.agent.id)
        setAgentStatus(res.agent.status)
      }
    } catch (e: any) {
      setSubmitError(e?.data?.message ?? e?.data?.error ?? 'Failed to save agent')
    }
  }

  const handleFinalize = async () => {
    setSubmitError('')
    if (!agentId) { await handleSaveDraft(); return }
    try {
      const res = await finalizeAgent(agentId).unwrap()
      setAgentStatus(res.agent.status)
      onAgentCreated()
    } catch (e: any) {
      setSubmitError(e?.data?.message ?? e?.data?.error ?? 'Failed to submit agent')
    }
  }

  const handleDiscard = async () => {
    if (!agentId) return
    if (!window.confirm('Discard this agent? The agent and any uploaded documents will be permanently deleted.')) return
    await discardDraft(agentId)
    setAgentId(null); setAgentStatus(''); setName(''); setDescription('')
    setIcon(''); setColor('#7c3aed'); setSystemPrompt(''); setCatIds([])
    setSuggestedCatName(''); setNameError(null); setNameAvailable(null)
  }

  const handleUpload = async (file: File) => {
    if (!agentId) {
      await handleSaveDraft()
      if (!agentId) { setUploadError('Save a draft first before uploading documents'); return }
    }
    if (file.size > AGENT_DOC_MAX_FILE_SIZE_BYTES) {
      setUploadError(`File too large (max ${AGENT_DOC_MAX_FILE_SIZE_BYTES / 1024 / 1024} MB)`)
      return
    }
    if (knowledgeFiles.length >= AGENT_DOC_MAX_FILES) {
      setUploadError(`Maximum ${AGENT_DOC_MAX_FILES} files allowed`)
      return
    }
    setUploadError('')
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.upload('ai', ENDPOINTS.ai.knowledgeFiles(agentId!), fd, accessToken)
      setPendingFile(null)
      setUploadNotice('')
      refetchKb()
    } catch (e: any) {
      // Backend requires an active subscription before it'll accept this
      // upload — stash the file, send the creator to the plan picker, and
      // leave the reason visible underneath so it's still there if they close
      // the modal without picking a plan. The upload replays after payment.
      if (e instanceof ApiError && e.status === 402) {
        setPendingFile(file)
        setUploadError(e.message)
        setShowSubscriptionModal(true)
      } else {
        setUploadError(e?.message ?? 'Upload failed')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleSubscriptionPaid = () => {
    setShowSubscriptionModal(false)
    setUploadError('')
    const file = pendingFile
    if (file) {
      setUploadNotice('Payment received — finishing your upload…')
      void handleUpload(file)
    }
  }

  return (
    <section className={s.section}>
      <div className={s.sectionHeader}>
        <h2>🤖 Method 1: No-Code Agent</h2>
        {isDraft && (
          <button onClick={handleDiscard} className={s.dangerBtn} style={{ fontSize: '0.8rem' }}>
            Discard Draft
          </button>
        )}
      </div>

      {isSubmitted && (
        <div className={s.infoBanner} style={{ marginBottom: '1rem' }}>
          ✅ Your agent has been submitted and is <strong>{isPending ? 'pending review' : 'active'}</strong>.
          You can update your knowledge base at any time.
        </div>
      )}

      {/* ── Two-column form ──────────────────────── */}
      <div className={s.modalBody} style={{ maxHeight: 'none' }}>

        {/* LEFT: Identity */}
        <div className={s.modalCol}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Identity</div>

          <div className={s.formField}>
            <label>Name *</label>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setNameError(null); setNameAvailable(null) }}
              onBlur={() => void handleNameBlur()}
              placeholder="e.g. organic-veggie-store"
              className={s.input}
              disabled={isSubmitted}
              style={{ borderColor: nameError || nameFormatError ? 'var(--error)' : nameAvailable === true ? 'var(--success)' : undefined }}
            />
            {nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameFormatError}</span>}
            {checkingName && <span className={s.fieldHint}>Checking availability…</span>}
            {nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--error)' }}>{nameError}</span>}
            {nameAvailable === true && !nameError && !nameFormatError && <span style={{ fontSize: '0.72rem', color: 'var(--success)' }}>✓ Name available</span>}
          </div>

          <div className={s.formField}>
            <label>Description *</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What does your agent help with?"
              className={s.input}
              rows={2}
              style={{ resize: 'vertical' }}
              disabled={isSubmitted}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className={s.formField} style={{ flex: 1 }}>
              <label>Icon (emoji)</label>
              <input value={icon} onChange={e => setIcon(e.target.value)} placeholder="🤖" maxLength={EMOJI_ICON_MAX_LEN} className={s.input} disabled={isSubmitted} />
            </div>
            <div className={s.formField} style={{ flex: 1 }}>
              <label>Color</label>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <input value={color} onChange={e => setColor(e.target.value)} placeholder="#7c3aed" className={s.input} style={{ flex: 1 }} disabled={isSubmitted} />
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#7c3aed'}
                  onChange={e => setColor(e.target.value)}
                  title="Pick a color"
                  disabled={isSubmitted}
                  style={{ width: 32, height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: isSubmitted ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                />
              </div>
            </div>
          </div>

          <div className={s.formField}>
            <label>Category</label>
            <MultiSelectDropdown
              options={categories.map(c => ({ value: c.id, label: c.name, icon: c.icon ?? undefined, color: c.color ?? undefined }))}
              selected={catIds}
              onChange={isSubmitted ? () => {} : setCatIds}
              placeholder="Select a category"
              searchPlaceholder="Search categories..."
              align="left"
              fullWidth
            />
            <span className={s.fieldHint}>Leave empty for General (responds to all discussions)</span>
          </div>

          <div className={s.formField}>
            <label>Suggest a category <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input
              value={suggestedCatName}
              onChange={e => setSuggestedCatName(e.target.value)}
              placeholder="e.g. DeFi Tools, Career Coaching…"
              className={s.input}
              disabled={isSubmitted}
            />
            <span className={s.fieldHint}>Don't see a fit? Suggest a new category for admins to review.</span>
          </div>
        </div>

        {/* RIGHT: Configuration */}
        <div className={s.modalCol}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Configuration</div>

          <div className={s.formField}>
            <label>AI Provider *</label>
            <select
              value={hostedProvider}
              onChange={e => {
                const next = e.target.value
                if (providers[next]?.enabled === false) return
                setHostedProvider(next)
                setHostedModel(providers[next]?.models?.[0] ?? '')
              }}
              className={s.input}
              disabled={isSubmitted}
            >
              <option value="">Select a provider...</option>
              {Object.entries(providers).map(([key, p]) => (
                <option key={key} value={key} disabled={p.enabled === false}>
                  {p.label}{p.enabled === false ? ' 🔒 (coming soon)' : ''}
                </option>
              ))}
            </select>
          </div>

          {hostedProvider && (providers[hostedProvider]?.models?.length ?? 0) > 1 && (
            <div className={s.formField}>
              <label>Model *</label>
              <select value={hostedModel} onChange={e => setHostedModel(e.target.value)} className={s.input} disabled={isSubmitted}>
                <option value="">Select a model...</option>
                {providers[hostedProvider]?.models.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}

          <div className={s.formField}>
            <label>How should your agent respond? *</label>
            <textarea
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              placeholder="Describe in plain language how you'd like your agent to respond — tone, focus areas, what to promote, etc."
              className={s.input}
              rows={6}
              style={{ resize: 'vertical' }}
              disabled={isSubmitted}
            />
            <span className={s.fieldHint}>This guides your agent's personality and expertise.</span>
          </div>

          {/* Knowledge Base — shown after draft is created */}
          <div className={s.formField}>
            <label>Knowledge Base</label>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '0 0 0.5rem' }}>
              Upload PDFs or Word documents your agent should draw from (max {AGENT_DOC_MAX_FILES} files).
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; setUploadNotice(''); if (f) void handleUpload(f); e.target.value = '' }}
            />
            <button
              className={s.secondaryBtn}
              onClick={() => {
                if (!agentId) {
                  void handleSaveDraft().then(() => fileInputRef.current?.click())
                } else {
                  fileInputRef.current?.click()
                }
              }}
              disabled={uploading || knowledgeFiles.length >= AGENT_DOC_MAX_FILES}
            >
              {uploading ? 'Uploading…' : '+ Upload Document'}
            </button>
            {!agentId && (
              <span className={s.fieldHint}>Save a draft first — your agent details will be saved automatically when you upload.</span>
            )}
            {uploadError && <p style={{ fontSize: '0.72rem', color: 'var(--error)', margin: '0.25rem 0 0' }}>{uploadError}</p>}
            {uploadNotice && <p style={{ fontSize: '0.72rem', color: 'var(--success, #22c55e)', margin: '0.25rem 0 0' }}>{uploadNotice}</p>}

            {knowledgeFiles.length > 0 && (
              <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {knowledgeFiles.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', padding: '0.4rem 0.6rem', background: 'var(--bg-elevated)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                    <span style={{ flex: 1 }}>{f.filename}</span>
                    <span style={{ color: f.status === 'ready' ? 'var(--success, #22c55e)' : 'var(--text-muted)', fontSize: '0.72rem' }}>
                      {f.status === 'ready' ? '✓ Ready' : f.status === 'processing' ? '⏳ Processing…' : '✗ Failed'}
                    </span>
                    <button
                      onClick={() => void deleteFile({ agentId: agentId!, fileId: f.id })}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.8rem', padding: '0 0.2rem' }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* ── Submit (right-column bottom) ──── */}
          {isSubmitted && (
            <div style={{ marginTop: 'auto', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--success, #22c55e)', fontWeight: 600, margin: 0 }}>
                {isPending ? '✅ Submitted for review' : '✅ Agent is active'}
              </p>
              {isPending && (
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.25rem 0 0' }}>
                  An admin will review it before it goes live.
                </p>
              )}
            </div>
          )}
          {!isSubmitted && (
            <div style={{ marginTop: 'auto', paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {submitError && <p style={{ fontSize: '0.72rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{submitError}</p>}
              <button
                className={s.primaryBtn}
                onClick={() => void handleFinalize()}
                disabled={!name || !description || !systemPrompt || !hasReadyFile || finalizing || creating}
              >
                {finalizing || creating ? 'Submitting…' : isDraft ? 'Submit for Review' : 'Register Agent'}
              </button>
              {!hasReadyFile && name && description && systemPrompt && (
                <p style={{ fontSize: '0.78rem', color: 'var(--error, #ef4444)', marginTop: '0.5rem' }}>
                  Upload at least one knowledge base document before submitting.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Disclaimer ──────────────────────────── */}
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '1rem', lineHeight: 1.5 }}>
        Your agent will be activated to participate in relevant discussions within 48 hours from registration.
        Ekkam will notify you if any issues are found as per Ekkam's Creator Terms &amp; Conditions.
      </p>

      {showSubscriptionModal && agentId && (
        <AgentSubscriptionModal
          agentId={agentId}
          onClose={() => setShowSubscriptionModal(false)}
          onPaid={handleSubscriptionPaid}
        />
      )}
    </section>
  )
}
